import { Alert as _Alert } from 'rcd-cpp-ui/ui/alert/Alert';
import * as React from 'react';
import { useActions } from './alertAction';
import { ErrorName } from '../../enums/ErrorName';
import { InvalidWorkspaceAlert } from './invalidWorkspaceError';
import { AlertType } from '../../types/AlertType';
import { useSelector } from '../../utils/useSelector';
import { Neo4jAlert } from './neo4jError';
import { FieldValidationAlert } from './fieldValidationError';
import { DuplicateFieldErrorAlert } from './duplicateFieldError';

const ErrorComponents = {
  [ErrorName.Neo4jError]: Neo4jAlert,
  [ErrorName.FieldValidationError]: FieldValidationAlert,
  [ErrorName.InvalidWorkspaceError]: InvalidWorkspaceAlert,
  [ErrorName.DuplicateFieldError]: DuplicateFieldErrorAlert
};

interface Props {
  id?: string;
  sticky?: boolean;
}

export const Alert = ({ id, sticky }: Props) => {
  const message = useSelector(s => {
    if (!id) return s.alert?.message;
    return (s.alert?.alertsWithId[id] || {}).message;
  });
  const alertType = useSelector(s => {
    if (!id) return s.alert?.alertType;
    return (s.alert?.alertsWithId[id] || {}).alertType;
  });
  if (!alertType || !message) return null;
  const { onClose } = useActions();
  return (
    <_Alert
      type={alertType}
      onClose={
        sticky
          ? null
          : () => {
              onClose(id);
            }
      }
    >
      {message}
    </_Alert>
  );
};

type Actions = {
  [x: string]: any;
};

export const useAlert = <A extends Actions>(
  { loading, setLoading, Loader, ...actions }: A,
  alertType?: AlertType
) => {
  const [message, setMessage] = React.useState<string | Error>(null);
  const Alert = React.useCallback(() => {
    if (!message) return null;
    let m: any = message;
    let type = alertType || 'error';
    if (m && typeof m == 'object') {
      const Component = ErrorComponents[m.name];
      if (Component != null) {
        return <Component error={m} onClose={() => setMessage(null)} />;
      }
      if (m.type) type = m.type;
      m = m.message;
    }
    return (
      <_Alert type={type} onClose={() => setMessage(null)}>
        {m}
      </_Alert>
    );
  }, [message]);
  const _actions = {} as any;
  if (actions) {
    Object.keys(actions).forEach(name => {
      const fn = actions[name];
      if (typeof fn != 'function') {
        _actions[name] = fn;
        return;
      }
      _actions[name] = async (...args) => {
        try {
          const r = fn(...args);
          if (r?.then) return await r;
        } catch (err) {
          setMessage(err);
          throw err;
        }
      };
    });
  }
  return {
    ...(_actions as A),
    message,
    setMessage,
    Alert,
    loading,
    setLoading,
    Loader
  };
};
